import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

export type ErrorStateAction = {
  label: string;
  onClick: () => void;
  // 主ボタンは contained、副ボタンは outlined。省略時は contained。
  variant?: "contained" | "outlined";
};

type Props = {
  severity: "error" | "warning" | "info";
  title: string;
  description: string;
  actions?: ErrorStateAction[];
};

// severity ごとのアイコンと円の色。notFound は warning、キャンセル不可などの案内は info を使う。
// 予約完了・キャンセル完了の確認アイコン (guest の ReservationSummaryCard) と同じく、塗りつぶした円に
// 白のグリフを載せる。成功が Check なのに対し、キャンセル不可は Close で対にする。
// 色はアプリごとに theme の palette.errorState で差し替える (shared-ui の defaultTheme.ts)。
const SEVERITY_STYLE = {
  error: { Icon: PriorityHighIcon, bgcolor: "errorState.error" },
  warning: { Icon: SearchOffIcon, bgcolor: "errorState.warning" },
  info: { Icon: CloseIcon, bgcolor: "errorState.info" },
} as const;

/**
 * エラー時にユーザーが次に取れる行動まで示す共通表示 (JPTP_CANSEI-1522)。
 * 円形アイコン + 見出し + 説明文 + ボタン群を縦に積む。
 *
 * 文言は翻訳済み文字列を props で受ける (QueryStateFallback の既存インターフェースと同じ)。
 * アクションが無い状態 (メール導線の案内のみ等) では actions を省略する。
 *
 * 置き換え前の MUI Alert が持っていた読み上げ (role="alert") を保つため、error / warning は
 * role="alert" (assertive)、案内に留まる info は role="status" (polite) を付与する。
 */
export function ErrorStateView({ severity, title, description, actions }: Props) {
  const { Icon, bgcolor } = SEVERITY_STYLE[severity];

  return (
    <Box
      role={severity === "info" ? "status" : "alert"}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 640,
        width: "100%",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 4,
          mb: 2,
        }}
      >
        <Icon sx={{ color: "#fff", fontSize: 24 }} />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, color: "errorState.title", mb: 1.5 }}>
        {title}
      </Typography>

      <Typography sx={{ color: "errorState.body", fontSize: 16, whiteSpace: "pre-line", mb: 4 }}>
        {description}
      </Typography>

      {actions && actions.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
            maxWidth: 320,
          }}
        >
          {actions.map((action) => (
            <Button
              // 1 画面のボタンは最大 2 つ (再読み込み / 店舗詳細に戻る) でラベルは必ず異なるため
              // ラベルを key にする。index は biome の noArrayIndexKey で禁止されている。
              key={action.label}
              variant={action.variant ?? "contained"}
              fullWidth
              onClick={action.onClick}
              sx={{ minHeight: 48 }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
}
